import {TranslateService} from '@ngx-translate/core';

import {DashboardChartUtils} from './dashboard-chart.utils';

import {ChartUtils} from '../../+postpayroll/check/key-metrics/charts/chart.utils';
import {StackedBar} from '../../+postpayroll/check/key-metrics/charts/column/stacked-bar';
import {Chart, ChartData} from '../../+postpayroll/check/key-metrics/key-metrics.interfaces';
import {LocalizationService} from '../../shared/localization/localization.service';
import {Scss} from '../../shared/utils/scss';
import {ChartBoxElement, DashboardBox, ResponseChartBox} from '../dashboard.interfaces';

export class PayrollSyncStatusChart extends StackedBar {

  private static readonly STATUSES = [
    'PAYROLL_SYNC_STATUS.off',
    'PAYROLL_SYNC_STATUS.blocked',
    'PAYROLL_SYNC_STATUS.compliant',
    'PAYROLL_SYNC_STATUS.in_progress',
    'PAYROLL_SYNC_STATUS.failed',
    'PAYROLL_SYNC_STATUS.success'
  ];

  constructor(chartBox: ResponseChartBox,
              translate: TranslateService,
              localization: LocalizationService) {

    const data = chartBox.params;
    const chart: Chart = ChartUtils.getEmptyChart();
    let total = 0;

    Object.keys(data).forEach((hCategory: string) => {
      if (!data[hCategory].length) {
        chart.data.push({
          category: null,
          name: hCategory,
          value: null
        });
      }

      data[hCategory]
        .sort((vCategory1, vCategory2) =>
          PayrollSyncStatusChart.STATUSES.indexOf(vCategory1.name) - PayrollSyncStatusChart.STATUSES.indexOf(vCategory2.name)
        )
        .forEach((vCategory: ChartBoxElement) => {
          total += vCategory.value;
          chart.data.push({
            category: vCategory.name,
            name: hCategory,
            value: vCategory.value,
            metadata: vCategory.link
          });
        });
    });
    chart.type = chartBox.cardType;

    super(chart, translate, localization, total);

    this.xAxis = [{
      type: 'value',
      axisLabel: {
        formatter: (value) => this.localization.localizeNumber(value, 'INTEGER')
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
      }
    }];

    this.yAxis = [{
      axisLabel: DashboardChartUtils.getAxisOptions(),
      data: this.yAxis.data.map((value: string) => translate.instant(value)),
      axisTick: {
        show: true,
      }
    }];
    this.grid = {
      left: 8,
      right: 16,
      top: 12,
      bottom: 48,
      containLabel: true
    };
    this.legend = {
      ...this.legend,
      type: 'scroll',
      bottom: 0,
      left: 'center',
      width: '100%',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 8,
      textStyle: {
        fontSize: 11
      },
      data: PayrollSyncStatusChart.STATUSES
        .filter((status: string) => this.chart.data.some((item: ChartData) => item.category === status))
    };
    this.animation = true;
    this.tooltip.position = DashboardChartUtils.getTooltipPositionFunc(<DashboardBox>chartBox);
    this.color = DashboardChartUtils.standardColors();

    DashboardChartUtils.setLinks(this.series, this.chart, this.categories);

    const colors = {
      'PAYROLL_SYNC_STATUS.off': Scss.variable('payroll-sync-off'),
      'PAYROLL_SYNC_STATUS.compliant': Scss.variable('payroll-sync-compliant'),
      'PAYROLL_SYNC_STATUS.blocked': Scss.variable('payroll-sync-blocked'),
      'PAYROLL_SYNC_STATUS.failed': Scss.variable('payroll-sync-failed'),
      'PAYROLL_SYNC_STATUS.success': Scss.variable('payroll-sync-success'),
      'PAYROLL_SYNC_STATUS.in_progress': Scss.variable('payroll-sync-in-progress')
    };

    this.series.forEach((serie, index) => {
      const barColor = colors[serie.name] || this.color[index];
      serie.itemStyle = {
        normal: {
          color: barColor,
          barBorderRadius: 5,
          barBorderWidth: 1,
          label: {
            show: true,
            formatter: this.getLabelFormatter(),
            textStyle: {
              color: DashboardChartUtils.textColor(barColor)
            }
          }
        }
      };
    });
  }

  public getLabelFormatter(): (value) => string {
    return (value) => value.value ? this.localization.localizeNumber(value.value, 'INTEGER') : '';
  }

  public sortCategories(): string[] {
    return ['PAYROLL_SYNC_STATUS.future', 'PAYROLL_SYNC_STATUS.active', 'PAYROLL_SYNC_STATUS.inactive'];
  }
}
